import React, { useCallback, useState, useEffect, useRef } from "react";
import { useTour } from '@reactour/tour';
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { SidebarProvider } from "@/components/ui/sidebar";
import { ProfileCompletionModal } from "@/components/ProfileCompletionModal";
import { OnboardingModal } from "@/components/OnboardingModal";
import { CommunityGateModal } from "@/components/CommunityGateModal";
import PixRenewalModal from "@/components/PixRenewalModal";
import PastDueBanner from "@/components/PastDueBanner";
import { isSubscriptionPastDue } from "@/lib/billing/isSubscriptionPastDue";
import { derivePixRecoveryState } from "@/lib/billing/pixRecoveryState";
import { resolvePastDueRecovery } from "@/lib/billing/resolvePastDueRecovery";
import { startStripeCustomerPortal } from "@/lib/billing/startStripeCustomerPortal";
import { useToast } from "@/hooks/use-toast";
import {
  isOnboardingPreviewEnabled,
  removeOnboardingPreviewParam,
} from "@/lib/onboarding/onboardingPreview";
import { shouldShowCommunityModal } from "@/lib/onboarding/shouldShowCommunityModal";
import {
  COMMUNITY_MODAL_DISMISSED_KEY,
  dismissCommunityPrompt,
} from "@/lib/community/communityPrompt";

import { useMyDataQuery } from "@/generated/graphql";
import { cacheProfilePicture } from "@/lib/profilePictureCache";
import { ViralizeQualificationProvider } from '@/components/viralize-qualification/ViralizeQualificationProvider';
import { currentEmbeddedApp } from '@/lib/app-embed/readEmbeddedApp';
import { DownloadUpsellProvider } from '@/components/upsell/DownloadUpsellProvider';
import { useCancelTourAutoOpen } from '@/contexts/tourAutoOpenContext';
import { useViralizeQualification } from '@/contexts/viralizeQualificationContext';
import { AssistantPanel } from '@/components/assistant/AssistantPanel';
import { useAssistantPanel } from '@/contexts/AssistantContext';
import { useAssistantAvailable } from '@/hooks/use-assistant-available';
import { useCampaignEntry } from '@/contexts/campaignEntryContext';

const PIX_RENEWAL_LAST_SHOWN_KEY = 'pix_renewal_last_shown';
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

interface LayoutProps {
  onStartTour?: () => void;
  children: React.ReactNode;
}

type MyDataQueryResult = ReturnType<typeof useMyDataQuery>;

interface LayoutContentProps extends LayoutProps {
  data: MyDataQueryResult['data'];
  loading: MyDataQueryResult['loading'];
  refetch: MyDataQueryResult['refetch'];
  qualificationActive: boolean;
}

const LayoutContent: React.FC<LayoutContentProps> = ({
  onStartTour,
  children,
  data,
  loading,
  refetch,
  qualificationActive,
}) => {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const campaignEntry = useCampaignEntry();
  const suppressAutomaticOnboarding = campaignEntry.loading || campaignEntry.skipOnboarding;
  // AI Studio shows its own chat; the sidebar steps aside only while it does.
  const { studioChatMounted } = useAssistantPanel();
  const assistantAvailable = useAssistantAvailable(
    data?.myData?.subscription?.plan?.features,
    !!data?.myData,
  );
  const navigate = useNavigate();
  const onboardingPreviewMode = isOnboardingPreviewEnabled({
    isDev: import.meta.env.DEV,
    search,
  });
  const { requestViralizeCourse } = useViralizeQualification();
  const { toast } = useToast();
  const [isPixRenewalModalOpen, setIsPixRenewalModalOpen] = useState(false);
  const [payingPastDue, setPayingPastDue] = useState(false);
  const [forcedOnboardingSession, setForcedOnboardingSession] = useState<
    number | null
  >(null);
  const onboardingReplaySequenceRef = useRef(0);
  const activeOnboardingInstanceRef = useRef('onboarding-default');
  const [onboardingCompletedLocally, setOnboardingCompletedLocally] =
    useState(false);
  const [communityModalOpen, setCommunityModalOpen] = useState(false);
  const onboardingInstanceKey = forcedOnboardingSession === null
    ? 'onboarding-default'
    : `onboarding-replay-${forcedOnboardingSession}`;

  // Expose a global function to trigger the onboarding modal from the console
  useEffect(() => {
    const onboardingWindow = window as Window & {
      __showOnboarding?: () => void;
    };
    const showOnboarding = () => {
      const nextSession = onboardingReplaySequenceRef.current + 1;
      onboardingReplaySequenceRef.current = nextSession;
      const nextInstanceKey = `onboarding-replay-${nextSession}`;
      activeOnboardingInstanceRef.current = nextInstanceKey;
      setForcedOnboardingSession(nextSession);
    };

    onboardingWindow.__showOnboarding = showOnboarding;
    return () => {
      delete onboardingWindow.__showOnboarding;
    };
  }, []);

  const isImpersonating = !!localStorage.getItem('adminToken');

  const handleStopImpersonating = () => {
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      localStorage.setItem('token', adminToken);
      localStorage.removeItem('adminToken');
      window.location.href = '/admin/users';
    }
  };

  // Check which fields are missing
  // Job title is collected atomically with the onboarding objective, so profile
  // completion only gates on name + phone.
  const profileMissingFields = !loading && data?.myData ? {
    name: !data.myData.name || data.myData.name.trim() === '',
    phoneNumber: !data.myData.phone_number || data.myData.phone_number.trim() === '',
  } : null;

  const needsProfileFields = !suppressAutomaticOnboarding && profileMissingFields && (
    profileMissingFields.name || profileMissingFields.phoneNumber
  );
  const shouldOpenOnboardingPreview =
    onboardingPreviewMode &&
    !loading &&
    !!data?.myData &&
    !needsProfileFields;

  // Onboarding is complete once the objective is set (daily volume is no longer asked).
  const needsOnboarding =
    !suppressAutomaticOnboarding &&
    !onboardingCompletedLocally &&
    !loading &&
    data?.myData &&
    !needsProfileFields &&
    !data.myData.onboarding_objective;

  const handleOnboardingComplete = (completedInstanceKey: string) => {
    if (completedInstanceKey !== activeOnboardingInstanceRef.current) {
      return false;
    }

    activeOnboardingInstanceRef.current = 'onboarding-default';
    setOnboardingCompletedLocally(true);
    setForcedOnboardingSession(null);

    if (shouldOpenOnboardingPreview) {
      navigate(
        { pathname, search: removeOnboardingPreviewParam(search) },
        { replace: true },
      );
      return;
    }

    try {
      void Promise.resolve(refetch()).catch((error) => {
        console.warn('Unable to refresh onboarding data after persistence', error);
      });
    } catch (error) {
      console.warn('Unable to refresh onboarding data after persistence', error);
    }
  };

  // Step 3: WhatsApp community — a dismissible prompt (no longer a hard gate),
  // shown only to clipping-channel users who have not joined. `dismissed: false`
  // here gives "is this user eligible at all"; the actual auto-open additionally
  // honours the per-user dismissal flag in localStorage (see the effect below).
  const communityEligible = !loading && !!data?.myData && !needsProfileFields && !needsOnboarding
    && !suppressAutomaticOnboarding
    && !shouldOpenOnboardingPreview
    && !qualificationActive
    && shouldShowCommunityModal({
      objective: data?.myData?.onboarding_objective,
      joinedCommunity: !!data?.myData?.joined_community,
      dismissed: false,
      pathname,
    });

  // Auto-open the community modal once for eligible users who haven't dismissed it.
  useEffect(() => {
    if (!communityEligible) {
      setCommunityModalOpen(false);
      return;
    }
    const dismissed = !!localStorage.getItem(COMMUNITY_MODAL_DISMISSED_KEY);
    if (!dismissed) setCommunityModalOpen(true);
  }, [communityEligible]);

  // Auto-show PIX renewal modal for pagarme users
  useEffect(() => {
    if (loading || !data?.myData) return;

    // Inside the mobile app payment is blocked and the app clears localStorage
    // on every page, so the cooldown below never holds: never auto-open there.
    if (currentEmbeddedApp()) return;

    if (qualificationActive) {
      setIsPixRenewalModalOpen(false);
      return;
    }

    if (shouldOpenOnboardingPreview) {
      setIsPixRenewalModalOpen(false);
      return;
    }

    // Don't show if profile completion, onboarding, or the community modal takes priority
    if (needsProfileFields || needsOnboarding || communityModalOpen) return;

    const subscription = data.myData.subscription;
    const hasLegacyPixOrder = subscription?.payment_provider === 'pagarme'
      && !!subscription?.pagarme_last_order_id;
    if (!hasLegacyPixOrder && !derivePixRecoveryState(subscription).canRecover) return;

    // Check 24h cooldown
    const lastShown = localStorage.getItem(PIX_RENEWAL_LAST_SHOWN_KEY);
    if (lastShown && Date.now() - Number(lastShown) < ONE_DAY_MS) return;

    // Auto-show and record timestamp
    localStorage.setItem(PIX_RENEWAL_LAST_SHOWN_KEY, String(Date.now()));
    setIsPixRenewalModalOpen(true);
  }, [
    loading,
    data,
    needsProfileFields,
    needsOnboarding,
    shouldOpenOnboardingPreview,
    communityModalOpen,
    qualificationActive,
  ]);

  const subscription = data?.myData?.subscription;
  const showPastDueBanner = !loading && isSubscriptionPastDue(subscription);

  const handlePastDuePay = async () => {
    const recovery = resolvePastDueRecovery(subscription);
    if (recovery.kind === 'store_manage') {
      window.open(recovery.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (recovery.kind !== 'stripe_portal') {
      setIsPixRenewalModalOpen(true);
      return;
    }
    try {
      setPayingPastDue(true);
      const url = await startStripeCustomerPortal(window.location.href, {
        preferOpenInvoice: true,
      });
      window.location.href = url;
    } catch (error) {
      console.error('Failed to open Stripe customer portal:', error);
      toast({
        title: t('billing.pastDueBanner'),
        description: t('billing.pastDuePortalError'),
        variant: 'destructive',
      });
    } finally {
      setPayingPastDue(false);
    }
  };

  const handleCommunityDismiss = () => {
    dismissCommunityPrompt(localStorage);
    setCommunityModalOpen(false);
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background flex w-full">
        <Sidebar />

        <div className="flex-1 flex flex-col min-w-0">
          {isImpersonating && (
            <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm">
              <span className="font-medium">{t('admin.users.impersonating')}</span>
              <button
                onClick={handleStopImpersonating}
                className="bg-white text-amber-600 px-3 py-1 rounded font-medium hover:bg-amber-50 transition-colors"
              >
                {t('admin.users.stopImpersonating')}
              </button>
            </div>
          )}
          {showPastDueBanner && (
            <PastDueBanner onPay={handlePastDuePay} paying={payingPastDue} />
          )}
          <Header onStartTour={onStartTour} />
          {children}
        </div>

        {/* AI Studio embeds the chat itself: one chat per page. */}
        {assistantAvailable && !studioChatMounted && (
          <AssistantPanel />
        )}
      </div>

      {/* Step 1: Profile completion (name, phone) */}
      {profileMissingFields && (
        <ProfileCompletionModal
          open={!!needsProfileFields}
          missingFields={profileMissingFields}
          currentName={data?.myData?.name}
          onComplete={refetch}
        />
      )}

      {/* Step 2: Segmented onboarding — shows after profile is complete */}
      <OnboardingModal
        key={onboardingInstanceKey}
        open={
          !!needsOnboarding ||
          forcedOnboardingSession !== null ||
          shouldOpenOnboardingPreview
        }
        onComplete={() => handleOnboardingComplete(onboardingInstanceKey)}
        onRequestViralizeCourse={
          forcedOnboardingSession !== null
            ? (trigger) =>
                requestViralizeCourse(trigger, { replayQuestionnaire: true })
            : requestViralizeCourse
        }
        previewMode={shouldOpenOnboardingPreview}
        profile={data?.myData}
      />

      {/* Step 3: WhatsApp community — dismissible prompt for clipping-channel users */}
      <CommunityGateModal
        open={communityModalOpen && communityEligible && !qualificationActive}
        onOpenChange={(o) => { if (!o) handleCommunityDismiss(); }}
        onJoined={() => { setCommunityModalOpen(false); refetch(); }}
      />

      {/* Auto-show PIX renewal modal */}
      <PixRenewalModal
        open={isPixRenewalModalOpen && !qualificationActive}
        onOpenChange={setIsPixRenewalModalOpen}
        onSuccess={async () => {
          await refetch();
        }}
        autoGenerate
        onCouponApplied={refetch}
      />


    </SidebarProvider>
  );
};

const Layout: React.FC<LayoutProps> = (props) => {
  const { data, loading, refetch } = useMyDataQuery();
  const { setIsOpen: setTourOpen } = useTour();
  // Shared with the Chrome extension popup (see profilePictureCache).
  const profilePicture = data?.myData?.profile_picture;
  useEffect(() => {
    if (data?.myData) cacheProfilePicture(localStorage, profilePicture);
  }, [data?.myData, profilePicture]);
  const cancelTourAutoOpen = useCancelTourAutoOpen();
  const [qualificationActive, setQualificationActive] = useState(false);
  const handleProfileSaved = useCallback(async () => {
    try {
      await refetch();
    } catch (error) {
      console.warn('Unable to refresh profile after qualification save', error);
    }
  }, [refetch]);
  const handleQualificationActiveChange = useCallback(
    (active: boolean) => {
      if (active) {
        cancelTourAutoOpen();
        setTourOpen(false);
      }
      setQualificationActive(active);
    },
    [cancelTourAutoOpen, setTourOpen],
  );

  return (
    <ViralizeQualificationProvider
      profileReady={!loading}
      isAdmin={data?.myData?.is_admin === true}
      profile={{
        name: data?.myData?.name,
        phone_number: data?.myData?.phone_number,
      }}
      onProfileSaved={handleProfileSaved}
      onActiveChange={handleQualificationActiveChange}
    >
      <DownloadUpsellProvider>
        <LayoutContent
          {...props}
          data={data}
          loading={loading}
          refetch={refetch}
          qualificationActive={qualificationActive}
        />
      </DownloadUpsellProvider>
    </ViralizeQualificationProvider>
  );
};

export default Layout;
